import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import barStockConsumptionReportService from '../services/barStockConsumptionReportService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
} from '../components/ui/collapsible';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BarStockConsumptionReportRunDetailPage = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const loadRun = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await barStockConsumptionReportService.getRun(runId);
      setRun(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '加载报表详情失败');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    loadRun();
  }, [loadRun]);

  const toggleRow = (index) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await barStockConsumptionReportService.exportRun(runId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout title="报表详情" activeTab="reports">
        <div className="max-w-[1600px] mx-auto py-12 px-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-4">加载报表详情...</p>
        </div>
      </Layout>
    );
  }

  if (error || !run) {
    return (
      <Layout title="报表详情" activeTab="reports">
        <div className="max-w-[1600px] mx-auto py-6 px-4">
          <Button variant="outline" onClick={() => navigate('/reports/bar-stock-consumption')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> 返回列表
          </Button>
          <div className="mt-6 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error || '报表不存在'}
          </div>
        </div>
      </Layout>
    );
  }

  const rows = run.rows || [];
  const summary = run.summary || {};

  return (
    <Layout title="棒料采购消耗报表详情" activeTab="reports">
      <div className="max-w-[1600px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/reports/bar-stock-consumption')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> 返回
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{run.report_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                运行编号：{run.run_id} · 生成时间：{formatDateTime(run.created_at)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} disabled={exporting || rows.length === 0}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="ml-2">导出 Excel</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">日期范围</p>
              <p className="text-lg font-semibold mt-1">
                {run.filters?.start_date} ~ {run.filters?.end_date}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">本期购入数量</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_purchase_quantity)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">本期领用数量</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_consumption_quantity)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">本期库存数量</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_inventory_quantity)}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              报表明细
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="whitespace-nowrap">来源类型</TableHead>
                    <TableHead className="whitespace-nowrap">发票日期</TableHead>
                    <TableHead className="whitespace-nowrap">入库日期</TableHead>
                    <TableHead className="whitespace-nowrap">采购单号</TableHead>
                    <TableHead className="whitespace-nowrap">采购单编号</TableHead>
                    <TableHead className="whitespace-nowrap">品牌</TableHead>
                    <TableHead className="whitespace-nowrap">品名</TableHead>
                    <TableHead className="whitespace-nowrap">specification</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入总价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用总价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存总价(未税)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={19} className="text-center py-8 text-muted-foreground">
                        没有记录
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row, index) => {
                    const isExpanded = expandedRows.has(index);
                    const hasAllocations = (row.allocations || []).length > 0;
                    return (
                      <React.Fragment key={index}>
                        <TableRow
                          className={hasAllocations ? 'cursor-pointer hover:bg-muted/50' : ''}
                          onClick={() => hasAllocations && toggleRow(index)}
                        >
                          <TableCell className="w-10">
                            {hasAllocations && (
                              <span className="inline-flex items-center justify-center">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge variant="secondary" className="font-normal">
                              {row.source_label || '采购入库'}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{row.invoice_date}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.receipt_date}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.purchase_order_number}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.purchase_order_code}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.brand}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.product_name}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.specification}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_quantity)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_unit_price)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_total)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_quantity)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_unit_price)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_total)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_quantity)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_unit_price)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_total)}</TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={19} className="p-0">
                              <Collapsible open={isExpanded}>
                                <CollapsibleContent>
                                  <div className="p-4">
                                    <p className="text-sm font-medium mb-2">领用/出库明细</p>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="whitespace-nowrap">出库日期</TableHead>
                                          <TableHead className="whitespace-nowrap">来源类型</TableHead>
                                          <TableHead className="whitespace-nowrap">领料单号/参考号</TableHead>
                                          <TableHead className="text-right whitespace-nowrap">数量</TableHead>
                                          <TableHead className="text-right whitespace-nowrap">单价(未税)</TableHead>
                                          <TableHead className="text-right whitespace-nowrap">金额(未税)</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {(row.allocations || []).map((alloc, allocIndex) => (
                                          <TableRow key={allocIndex}>
                                            <TableCell className="whitespace-nowrap">{alloc.outbound_date}</TableCell>
                                            <TableCell className="whitespace-nowrap">
                                              <Badge variant="outline" className="font-normal">
                                                {alloc.source_label || alloc.source_type}
                                              </Badge>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">{alloc.source_reference || '-'}</TableCell>
                                            <TableCell className="text-right whitespace-nowrap">{formatNumber(alloc.quantity)}</TableCell>
                                            <TableCell className="text-right whitespace-nowrap">{formatNumber(alloc.unit_price)}</TableCell>
                                            <TableCell className="text-right whitespace-nowrap">{formatNumber(alloc.total)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {rows.length > 0 && (
                    <TableRow className="font-semibold bg-muted/30">
                      <TableCell colSpan={9} className="text-right">合计</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_purchase_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_purchase_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_consumption_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_consumption_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_inventory_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_inventory_total)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BarStockConsumptionReportRunDetailPage;
